import { useEffect, useState, type ReactNode } from 'react';
import { Button, Card, Field, Input, Select, Toolbar } from '@biovet/ui';
import {
  allCurrencies,
  baseCurrency,
  companyToday,
  monthOf,
  rateFor,
  shiftMonth,
} from '../lib/reference-data.js';

/**
 * Two presentational shapes the design uses on nearly every document screen.
 *
 * They are not in `@biovet/ui` because they are layout, not vocabulary: a
 * component library earns its place by owning decisions (what a debit looks
 * like, how dense a row is), and these own none. When a third screen wants them
 * to behave differently they should stay here and diverge, not grow props in the
 * design system.
 */

/** Label above value, for the strip of facts across a document header. */
export function Fact({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div>
      <div className="bb-eyebrow pb-1.5">{label}</div>
      <div className="text-[13px] font-medium text-[var(--ink)]">{value}</div>
    </div>
  );
}

/** Grid of `Fact`s, six across on a wide screen and two on a phone. */
export function FactGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 xl:grid-cols-6">{children}</div>
  );
}

/** Header cell matching the design system's grid header. */
export function Th({
  children,
  align = 'left',
  width,
}: {
  children: ReactNode;
  // `center` is for a column of checkboxes — a figure column is right-aligned so
  // the digits line up, and a heading over a tick is centred over the tick.
  align?: 'left' | 'right' | 'center';
  width?: string;
}) {
  return (
    <th
      style={width ? { width } : undefined}
      className={`bb-eyebrow border-b border-[var(--line-2)] px-2.5 py-2 font-medium first:px-[var(--pad)] last:px-[var(--pad)] ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

/**
 * The currency picker and its rate, as one control.
 *
 * They were written out by hand on four screens — a `<Select>` with two literal
 * `<option>`s and an `<Input>` beside it — which is why a currency added under
 * Setup → Currencies was postable by the database and unofferable by the app.
 * The list is the register, so a new currency appears here the moment somebody
 * adds one.
 *
 * **Choosing a currency applies its rate.** That is the behaviour worth having
 * in one place: picking USD fills in the live USD rate, picking base fills in 1,
 * and picking a currency `fx_rates` has never carried clears the field rather
 * than leaving the previous currency's number sitting under a new code. A stale
 * rate under a changed currency is the failure this is built to prevent — it
 * converts at a plausible number and nothing on screen says it is the wrong one.
 *
 * The rate stays editable for the entry that legitimately differs from the
 * session rate. It is disabled only on base, where it is 1 by definition.
 */
export function CurrencyRateFields({
  currency,
  fxRate,
  onChange,
  size = 'sm',
  label = 'Currency',
  rateLabel = 'Rate',
  rateHint,
  disabled = false,
}: {
  currency: string;
  fxRate: string;
  /** Both together: picking a currency changes the rate, so one callback. */
  onChange: (next: { currency: string; fxRate: string }) => void;
  size?: 'sm' | 'md';
  label?: string;
  rateLabel?: string;
  rateHint?: string;
  disabled?: boolean;
}) {
  const base = baseCurrency();
  const options = allCurrencies();
  const onBase = currency === base;

  // Before /api/context answers, the register is empty. Offering the currently
  // selected code alone is better than an empty dropdown that looks broken.
  const list =
    options.length > 0
      ? options
      : [{ code: currency || base, name: '', isBase: currency === base, rate: null }];

  return (
    <>
      <Field label={label}>
        <Select
          size={size}
          value={currency}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            onChange({ currency: next, fxRate: rateFor(next) ?? '' });
          }}
        >
          {list.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={rateLabel}
        hint={
          rateHint ??
          (onBase
            ? undefined
            : rateFor(currency) === null
              ? `No ${currency}/${base} rate on file`
              : 'base = txn × rate')
        }
      >
        <Input
          size={size}
          numeric
          value={fxRate}
          disabled={disabled || onBase}
          onChange={(e) => onChange({ currency, fxRate: e.target.value })}
        />
      </Field>
    </>
  );
}

/**
 * The window a register is being read through — two dates and the four controls
 * that move them.
 *
 * The Trial Balance and the General Ledger each grew their own copy of this,
 * because a report with movement in it and no stated window is showing the whole
 * book and saying nothing about it. The five operational registers had the same
 * problem and none of the controls: `Sales Invoices` drew every invoice ever
 * raised, got slower every year, and could not answer "what did we sell in
 * March" at all.
 *
 * It is one control here rather than a fifth and sixth copy. `Whole book`
 * renders only when a window is set, because it is the escape from a default —
 * offering it on a register that is already showing everything is a button that
 * does nothing.
 *
 * An empty box is read by the API as "no bound on that side", which is why
 * clearing both is the same request the registers used to make unconditionally.
 */
export function DateWindow({
  range,
  onChange,
  label = 'Whole book',
}: {
  range: { from: string; to: string };
  onChange: (next: { from: string; to: string }) => void;
  /** What the escape button says — `Whole book` on a ledger, `All time` where
   *  the register is not the book. */
  label?: string;
}) {
  const anchor = range.from || companyToday();
  return (
    <>
      <Field label="From">
        <Input
          size="sm"
          type="date"
          value={range.from}
          aria-label="From date"
          onChange={(e) => onChange({ ...range, from: e.target.value })}
        />
      </Field>
      <Field label="To">
        <Input
          size="sm"
          type="date"
          value={range.to}
          aria-label="To date"
          onChange={(e) => onChange({ ...range, to: e.target.value })}
        />
      </Field>
      <Button
        size="sm"
        variant="ghost"
        title="Previous month"
        onClick={() => onChange(shiftMonth(anchor, -1))}
      >
        ‹
      </Button>
      <Button
        size="sm"
        variant="ghost"
        title="Next month"
        onClick={() => onChange(shiftMonth(anchor, 1))}
      >
        ›
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onChange(monthOf(companyToday()))}>
        This month
      </Button>
      {(range.from || range.to) && (
        <Button size="sm" variant="ghost" onClick={() => onChange({ from: '', to: '' })}>
          {label}
        </Button>
      )}
    </>
  );
}

/**
 * A value that lags the one being typed, for a box whose every keystroke would
 * otherwise be a request.
 *
 * The registers filter rows the browser already holds, so they need none of
 * this. The audit trail and the activity feed search **in SQL** — they have to,
 * since the screen holds the newest few hundred rows out of a log that only
 * grows — and a `useResource` keyed on the raw box fires a query per character.
 *
 * `useResource` already discards a response that is not the newest, so this is
 * about the queries not being made rather than about which answer paints.
 */
export function useDebounced<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * The window bar the five operational registers wear, and the sentence that says
 * what is on screen.
 *
 * The sentence is the part worth having. A register that silently shows one
 * month looks exactly like a register that has one month of data, and the
 * difference matters most to the person who has just been told a document is
 * missing. `count` is what the screen is actually displaying — after the chip
 * filter, not before — so the two never disagree.
 */
export function RegisterWindow({
  range,
  onChange,
  count,
  noun,
}: {
  range: { from: string; to: string };
  onChange: (next: { from: string; to: string }) => void;
  /** How many rows the register is showing, after its own filtering. */
  count: number;
  /** Singular; the `s` is added here. */
  noun: string;
}) {
  const windowed = range.from || range.to;
  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <Toolbar>
        <DateWindow range={range} onChange={onChange} label="All time" />
        <div className="flex-1" />
        <span className="text-[12px] text-[var(--ink-3)]">
          {count} {noun}
          {count === 1 ? '' : 's'}{' '}
          {windowed
            ? `dated ${range.from || 'the start of the book'} to ${range.to || 'today'}`
            : 'in the whole book'}
        </span>
      </Toolbar>
    </Card>
  );
}
